import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { isUnauthorizedError } from '@/lib/authUtils';
import { apiRequest } from '@/lib/queryClient';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { HelpCircle, Send } from 'lucide-react';
import { useState } from 'react';

interface HelpRequestModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const CATEGORIES = [
  { value: 'technical', label: 'Technical Issue' },
  { value: 'account', label: 'Account Problem' },
  { value: 'feature', label: 'Feature Request' },
  { value: 'bug', label: 'Bug Report' },
  { value: 'other', label: 'Other' },
];

const PRIORITIES = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'urgent', label: 'Urgent' },
];

export default function HelpRequestModal({
  isOpen,
  onClose,
}: HelpRequestModalProps) {
  const [category, setCategory] = useState('');
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState('medium');
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const helpRequestMutation = useMutation({
    mutationFn: async (data: {
      category: string;
      subject: string;
      description: string;
      priority: string;
    }) => {
      const response = await apiRequest('POST', '/api/help-requests', data);
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/help-requests'] });
      toast({
        title: 'Help Request Sent',
        description:
          'Your help request has been sent to the system administrators.',
      });
      handleClose();
    },
    onError: error => {
      if (isUnauthorizedError(error)) {
        toast({
          title: 'Unauthorized',
          description: 'You are logged out. Logging in again...',
          variant: 'destructive',
        });
        setTimeout(() => {
          window.location.href = '/api/login';
        }, 500);
        return;
      }
      toast({
        title: 'Error',
        description: 'Failed to send help request',
        variant: 'destructive',
      });
    },
  });

  const handleSubmit = () => {
    if (!category || !subject.trim() || !description.trim()) {
      toast({
        title: 'Error',
        description: 'Please fill in all required fields',
        variant: 'destructive',
      });
      return;
    }

    helpRequestMutation.mutate({
      category,
      subject: subject.trim(),
      description: description.trim(),
      priority,
    });
  };

  const handleClose = () => {
    setCategory('');
    setSubject('');
    setDescription('');
    setPriority('medium');
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold flex items-center">
            <HelpCircle className="w-5 h-5 mr-2 text-blue-600" />
            Request Help from System Admin
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label
              htmlFor="category"
              className="text-sm font-medium text-gray-700"
            >
              Category *
            </Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger className="mt-1">
                <SelectValue placeholder="Select a category" />
              </SelectTrigger>
              <SelectContent>
                {CATEGORIES.map(cat => (
                  <SelectItem key={cat.value} value={cat.value}>
                    {cat.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label
              htmlFor="subject"
              className="text-sm font-medium text-gray-700"
            >
              Subject *
            </Label>
            <Input
              id="subject"
              value={subject}
              onChange={e => setSubject(e.target.value)}
              placeholder="Brief description of your issue"
              className="mt-1"
              data-testid="input-help-subject"
            />
          </div>

          <div>
            <Label
              htmlFor="priority"
              className="text-sm font-medium text-gray-700"
            >
              Priority
            </Label>
            <Select value={priority} onValueChange={setPriority}>
              <SelectTrigger className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PRIORITIES.map(pri => (
                  <SelectItem key={pri.value} value={pri.value}>
                    {pri.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label
              htmlFor="description"
              className="text-sm font-medium text-gray-700"
            >
              Description *
            </Label>
            <Textarea
              id="description"
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="Please provide detailed information about your issue or request..."
              className="mt-1 min-h-[120px]"
              data-testid="textarea-help-description"
            />
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <Button
              variant="outline"
              onClick={handleClose}
              data-testid="button-cancel-help-request"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={
                helpRequestMutation.isPending ||
                !category ||
                !subject.trim() ||
                !description.trim()
              }
              data-testid="button-send-help-request"
            >
              <Send className="w-4 h-4 mr-2" />
              {helpRequestMutation.isPending ? 'Sending...' : 'Send Request'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
